import { Box, Drawer, type Theme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { DRAWER_CONFIG, SIDEBAR_OPTION, type SidebarType } from '../../../../config/sidebar';
import {
  NEOBET_HEADER_HEIGHT,
  NEOBET_MOBILE_BOTTOM_NAV_HEIGHT,
  NEOBET_MOBILE_HEADER_HEIGHT,
} from '../../../../config/skinRegistry/configs';
import useBreakpoints from '../../../../hooks/useBreakpoints';
import { type DrawerLayoutProps } from '../../../layouts/DrawerLayout';
import { NEOBET_COMPACT_DRAWER_WIDTH, NEOBET_DESKTOP_DRAWER_WIDTH } from '../constants';
import { SidebarPresentationProvider } from '../contexts/SidebarPresentationContext';
import { getNeobetSidebarResizeTransition } from '../sidebarMotion';

const compactEligibleSidebarTypes = new Set<SidebarType>([
  SIDEBAR_OPTION.SPORTS,
  SIDEBAR_OPTION.IN_PLAY,
  SIDEBAR_OPTION.IN_PLAY_MATCH,
]);

const mobileDrawerModalProps = { hideBackdrop: true } as const;
const mobileDrawerPaperProps = { 'data-testid': 'neobet-mobile-drawer-panel' } as const;
const noop = () => undefined;

const createStyles = (sidebarType: SidebarType, isCompact: boolean) => {
  const canCompact = compactEligibleSidebarTypes.has(sidebarType);
  const expandedDrawerWidth = canCompact ? NEOBET_DESKTOP_DRAWER_WIDTH : DRAWER_CONFIG[sidebarType].width;
  const drawerWidth = isCompact ? NEOBET_COMPACT_DRAWER_WIDTH : expandedDrawerWidth;

  return {
    desktop: {
      flexShrink: 0,
      width: drawerWidth,
      minWidth: drawerWidth,
      pt: `${NEOBET_HEADER_HEIGHT}px`,
      height: '100vh',
      backgroundColor: 'neutral.700',
      transition: getNeobetSidebarResizeTransition(isCompact),
      willChange: 'width',
    },
    shell: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      backgroundColor: { xs: 'background.default', md: 'neutral.700' },
      px: { xs: 2, md: 0 },
      py: { xs: 1.5, md: 0 },
    },
    mobileDrawer: {
      zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
      '& .MuiDrawer-paper': {
        top: `var(--header-height, ${NEOBET_MOBILE_HEADER_HEIGHT}px)`,
        bottom: `var(--mobile-bottom-nav-height, ${NEOBET_MOBILE_BOTTOM_NAV_HEIGHT}px)`,
        width: '100vw',
        height: 'auto',
        backgroundColor: 'background.default',
        boxShadow: 'none',
      },
    },
    content: {
      width: canCompact ? { xs: 1, md: NEOBET_DESKTOP_DRAWER_WIDTH } : 1,
      flexShrink: 0,
      borderRadius: { xs: 3, md: 0 },
      backgroundColor: { xs: 'neutral.800', md: 'transparent' },
      overflow: { xs: 'hidden', md: 'visible' },
    },
  };
};

const DrawerLayout = ({ isOpen, isCompact = false, toggleVisibility, children, sidebarType }: DrawerLayoutProps) => {
  const { isMobile } = useBreakpoints();
  const canCompact = compactEligibleSidebarTypes.has(sidebarType);
  const resolvedCompact = canCompact && !isMobile && isCompact;
  const styles = createStyles(sidebarType, resolvedCompact);
  const handleClose = useCallback(() => toggleVisibility(false), [toggleVisibility]);

  const drawerBody = useMemo(
    () => (
      <SidebarPresentationProvider closeDrawer={isMobile ? handleClose : noop} isCompact={resolvedCompact}>
        <Box sx={styles.shell}>
          <Box sx={styles.content}>{children}</Box>
        </Box>
      </SidebarPresentationProvider>
    ),
    [children, handleClose, isMobile, resolvedCompact, styles]
  );

  if (isMobile) {
    return (
      <Drawer
        anchor='left'
        open={isOpen}
        variant='temporary'
        onClose={handleClose}
        ModalProps={mobileDrawerModalProps}
        sx={styles.mobileDrawer}
        slotProps={{
          paper: mobileDrawerPaperProps,
        }}
      >
        {drawerBody}
      </Drawer>
    );
  }

  return (
    <Box component='aside' sx={styles.desktop} data-testid='neobet-desktop-drawer'>
      {drawerBody}
    </Box>
  );
};

export default DrawerLayout;
